import { Link } from 'react-router-dom';
import { ChevronRight, BarChart3 } from 'lucide-react';

import { useSeasonsQuery } from '@/services/endpoints/leaderboard';
import { useGameSelection } from '@/hooks/useGameSelection';
import { GameHeader } from '@/components/games/GameHeader';
import { Loader } from '@/components/common/Loader';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';

export function Leaderboards() {
  const { activeGame } = useGameSelection();
  const games = activeGame ? String(activeGame.id) : undefined;

  const { data, isLoading, isError } = useSeasonsQuery({
    games,
    is_active: true,
  });

  const seasons = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">Leaderboards</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Active seasons for the selected game.
        </p>
      </header>

      <GameHeader />

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader />
        </div>
      )}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          Failed to load seasons.
        </div>
      )}

      {!isLoading && !isError && seasons.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          No active leaderboards for this game.
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {seasons.map((season) => (
          <li key={season.id}>
            <Link
              to={`/leaderboards/${season.id}`}
              className={cn(
                'flex items-center gap-3 rounded-2xl bg-bg-secondary p-3',
                'transition-opacity active:opacity-80',
              )}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-bg-tertiary">
                {season.image ? (
                  <img
                    src={cdnImage(season.image)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <BarChart3 aria-hidden className="h-6 w-6 text-brand" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-fg">{season.title}</p>
                {season.description && (
                  <p className="line-clamp-1 text-xs text-fg-muted">{season.description}</p>
                )}
              </div>
              <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
